import { Avatar } from "components/DesignSystem/AvatarGroup/Avatar";
import { Button } from "components/DesignSystem/Button/Button";
import { Combobox } from "components/DesignSystem/Combobox/Combobox";
import Modal from "components/DesignSystem/Modal/Modal";
import { TextInput } from "components/DesignSystem/TextInput/TextInput";
import { Form, Formik, FormikValues } from "formik";
import { sendToEmailSchema } from "formValidations/sendToEmailSchema";
import { useToast } from "hooks/useToast";
import { useMemo } from "react";
import {
  useGetAllChannelMemberQuery,
  useSendMessageToEmailMutation,
} from "store/apis/chat";
import {
  MessageUIComponentProps,
  useChannelStateContext,
} from "stream-chat-react";
import { BackendError } from "types/utils/error";

export const SendToEmail = ({
  isOpen,
  close,
  message,
}: {
  isOpen: boolean;
  close: () => void;
  message: MessageUIComponentProps["message"] & {
    forwarded_message?: MessageUIComponentProps["message"];
  };
}) => {
  const { channel } = useChannelStateContext();
  const groupId = channel.data?.group_uuid! as string;
  const { data: members, isLoading } = useGetAllChannelMemberQuery(
    {
      groupId: groupId,
      channelId: channel.id!,
    },
    {
      skip: !channel.id || !channel.data?.group_uuid,
    }
  );
  const membersWithEmail = members?.filter(
    ({ email, is_archived_user }) => Boolean(email) && !is_archived_user
  );
  const [sendMessageToEmail, { isLoading: isSending }] =
    useSendMessageToEmailMutation();
  const { alertToast, successToast } = useToast();

  const sendMessage = async (values: FormikValues) => {
    try {
      const parsedMembers = values.members.map((member: string) =>
        JSON.parse(member)
      );
      const payload = {
        email_subject: values.subject,
        email_text: message.text || "",
        message_type:
          message.attachments && message.attachments.length > 0
            ? "FILE"
            : "MESSAGE",
        to_users: parsedMembers,
      };
      await sendMessageToEmail({
        channelId: channel.id!,
        messageId: message.id,
        groupId,
        payload,
      }).unwrap();
      successToast({ message: "Message sent successfully!" });
      close();
    } catch (e) {
      alertToast({ message: (e as BackendError).data?.error?.message });
    }
  };

  const options = useMemo(
    () =>
      membersWithEmail?.map((member) => ({
        label: member.name,
        value: JSON.stringify(member),
      }))!,
    [JSON.stringify(membersWithEmail)]
  );

  return (
    <Modal.Root open={isOpen} onOpenChange={close} modal={false}>
      <Modal.Content useCustomOverlay>
        <Formik
          initialValues={{ members: [] as string[], subject: "" }}
          onSubmit={sendMessage}
          validationSchema={sendToEmailSchema}
          validateOnChange={false}
          validateOnBlur
          validateOnMount={false}
        >
          {({ setFieldValue, submitForm, values }) => {
            return (
              <Form>
                <Modal.Header>
                  <Modal.Title>Send to Email</Modal.Title>
                  <Modal.Close />
                </Modal.Header>
                <Modal.Body className="t-space-y-4 t-overflow-hidden t-max-h-[700px]">
                  <div className="t-text-body-sm t-text-text-60">
                    Please select the customer’s email address & they will
                    receive an email with the chat message sent.
                  </div>
                  <Combobox
                    withForm
                    label="To"
                    components={{
                      ClearIndicator: () => null,
                    }}
                    isMulti
                    placeholder="Select"
                    name="members"
                    required
                    value={
                      values.members.length > 0
                        ? options.filter((option) =>
                            (values.members as string[]).includes(option.value)
                          )
                        : null
                    }
                    options={options}
                    actions={
                      <div>
                        <Button
                          customType="text"
                          size="small"
                          onClick={() => setFieldValue("members", [])}
                        >
                          Reset Selection
                        </Button>
                        <Button
                          customType="text"
                          size="small"
                          onClick={() =>
                            setFieldValue(
                              "members",
                              options.map((option) => option.value)
                            )
                          }
                        >
                          Select All
                        </Button>
                      </div>
                    }
                    isLoading={isLoading}
                    menuPortalTarget={document.body}
                    styles={{
                      menuList: (base) => ({
                        ...base,
                        maxHeight: "200px",
                        overflowY: "auto",
                      }),
                      valueContainer: (base) => ({
                        ...base,
                        maxHeight: "200px",
                        overflowY: "auto",
                      }),
                    }}
                  />
                  <TextInput
                    name="subject"
                    placeholder="Subject"
                    label="Subject"
                    required
                  />
                </Modal.Body>
                <Modal.FooterButtonGroup>
                  <Modal.RawClose asChild>
                    <Button>Cancel</Button>
                  </Modal.RawClose>
                  <Button
                    customType="primary"
                    type="submit"
                    disabled={isSending}
                    isLoading={isSending}
                    onClick={submitForm}
                  >
                    Confirm
                  </Button>
                </Modal.FooterButtonGroup>
              </Form>
            );
          }}
        </Formik>
      </Modal.Content>
    </Modal.Root>
  );
};
